import { TS_REGEX } from './constants';
import type { FormatDetectionResult } from './types';

/**
 * Heuristically inspects LRC text to determine structure and recommend optimal conversion:
 * - 'empty': no text provided
 * - 'plain': text has lines but zero timestamp tags
 * - 'merged': lines contain combined timestamps (e.g. `[00:10.00][00:20.00] Chorus`) -> Suggests Unmerge
 * - 'unmerged': lines contain chronological single timestamps -> Suggests Merge
 *
 * @param rawText Raw LRC input string.
 * @returns Comprehensive FormatDetectionResult with confidence and recommendation.
 */
export function detectLrcFormat(rawText: string): FormatDetectionResult {
  const trimmed = rawText.trim();
  if (!trimmed) {
    return {
      format: 'empty',
      recommendedMode: null,
      totalLines: 0,
      timestampLinesCount: 0,
      multiTimestampLinesCount: 0,
      hasRepeatedLyrics: false,
      confidence: 'high',
      summary: ''
    };
  }

  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim().length > 0);
  let timestampLinesCount = 0;
  let multiTimestampLinesCount = 0;
  const lyricOccurrences = new Map<string, number>();

  for (const line of lines) {
    const tsMatches = line.match(TS_REGEX);
    if (tsMatches && tsMatches.length > 0) {
      timestampLinesCount++;
      if (tsMatches.length > 1) {
        multiTimestampLinesCount++;
      }
      const lyricText = line.replace(TS_REGEX, '').trim().toLowerCase();
      if (lyricText.length > 2) {
        lyricOccurrences.set(lyricText, (lyricOccurrences.get(lyricText) || 0) + 1);
      }
    }
  }

  if (timestampLinesCount === 0) {
    return {
      format: 'plain',
      recommendedMode: null,
      totalLines: lines.length,
      timestampLinesCount: 0,
      multiTimestampLinesCount: 0,
      hasRepeatedLyrics: false,
      confidence: 'high',
      summary: 'Plain Lyrics (No Timestamps)'
    };
  }

  let repeatedLyricsCount = 0;
  for (const count of lyricOccurrences.values()) {
    if (count > 1) {
      repeatedLyricsCount += count;
    }
  }

  if (multiTimestampLinesCount > 0) {
    return {
      format: 'merged',
      recommendedMode: 'unmerge',
      totalLines: lines.length,
      timestampLinesCount,
      multiTimestampLinesCount,
      hasRepeatedLyrics: repeatedLyricsCount > 0,
      confidence: multiTimestampLinesCount >= 2 ? 'high' : 'medium',
      summary: 'Multi-Timestamp (Merged)'
    };
  }

  return {
    format: 'unmerged',
    recommendedMode: 'merge',
    totalLines: lines.length,
    timestampLinesCount,
    multiTimestampLinesCount: 0,
    hasRepeatedLyrics: repeatedLyricsCount > 0,
    confidence: 'high',
    summary: 'Chronological (Unmerged)'
  };
}
